import { useState, useEffect, useRef } from 'react';
import { superAdminApi } from '../../lib/api';

interface Company {
  id: number;
  name: string;
  clerkOrgId: string;
  isActive: boolean;
  createdAt: number;
  storeCount: number;
  hasToken: boolean;
  tokenDescription: string | null;
}

interface StoreInfo {
  id: number;
  name: string;
  accessToken: string;
  isDemo: boolean;
  createdAt: number;
}

export default function SuperAdmin() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTokenId, setEditingTokenId] = useState<number | null>(null);
  const [tokenInput, setTokenInput] = useState('');
  const [tokenDescInput, setTokenDescInput] = useState('');
  const [tokenSaving, setTokenSaving] = useState(false);
  const [expandedStoresId, setExpandedStoresId] = useState<number | null>(null);
  const [loadingStoresId, setLoadingStoresId] = useState<number | null>(null);
  const [storesByCompany, setStoresByCompany] = useState<Record<number, StoreInfo[]>>({});
  const [togglingStoreIds, setTogglingStoreIds] = useState<Set<number>>(new Set());
  const [addingStoreForCompanyId, setAddingStoreForCompanyId] = useState<number | null>(null);
  const [newStoreName, setNewStoreName] = useState('');
  const [savingStore, setSavingStore] = useState(false);
  const [deletingStoreIds, setDeletingStoreIds] = useState<Set<number>>(new Set());
  const [copiedStoreId, setCopiedStoreId] = useState<number | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showProvision, setShowProvision] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState<{
    userEmail: string;
    companyName: string;
  } | null>(null);
  const [provisionForm, setProvisionForm] = useState({
    companyName: '',
    userEmail: '',
    soundrawToken: '',
    tokenDescription: '',
  });

  useEffect(() => {
    void load();
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await superAdminApi.listCompanies();
      setCompanies(data);
    } catch {
      // handled by global toast
    } finally {
      setLoading(false);
    }
  }

  async function refresh() {
    setLoading(true);
    try {
      const data = await superAdminApi.listCompanies();
      setCompanies(data);
    } catch {
      // handled by global toast
    } finally {
      setLoading(false);
    }
  }

  function storeUrl(accessToken: string) {
    return `${window.location.origin}/play/${accessToken}`;
  }

  function copyStoreUrl(storeId: number, accessToken: string) {
    void navigator.clipboard.writeText(storeUrl(accessToken));
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    setCopiedStoreId(storeId);
    copyTimeoutRef.current = setTimeout(() => setCopiedStoreId(null), 2000);
  }

  function startEditToken(company: Company) {
    setEditingTokenId(company.id);
    setTokenInput('');
    setTokenDescInput(company.tokenDescription ?? '');
  }

  async function saveToken(id: number) {
    setTokenSaving(true);
    try {
      await superAdminApi.updateCompanyToken(
        id,
        tokenInput.trim() || null,
        tokenDescInput.trim() || null,
      );
      setEditingTokenId(null);
      await refresh();
    } catch {
      // handled by global toast
    } finally {
      setTokenSaving(false);
    }
  }

  async function provision() {
    setProvisioning(true);
    try {
      const result = await superAdminApi.provision({
        companyName: provisionForm.companyName,
        userEmail: provisionForm.userEmail,
        soundrawToken: provisionForm.soundrawToken,
        tokenDescription: provisionForm.tokenDescription || undefined,
      });
      setProvisionResult({
        userEmail: result.userEmail,
        companyName: result.companyName,
      });
      setProvisionForm({ companyName: '', userEmail: '', soundrawToken: '', tokenDescription: '' });
      setShowProvision(false);
      await refresh();
    } catch {
      // handled by global toast
    } finally {
      setProvisioning(false);
    }
  }

  async function toggleStores(companyId: number) {
    if (expandedStoresId === companyId) {
      setExpandedStoresId(null);
      return;
    }
    setExpandedStoresId(companyId);
    setLoadingStoresId(companyId);
    try {
      const stores = await superAdminApi.listCompanyStores(companyId);
      setStoresByCompany((prev) => ({ ...prev, [companyId]: stores }));
    } catch {
      setExpandedStoresId(null);
      // handled by global toast
    } finally {
      setLoadingStoresId(null);
    }
  }

  async function toggleStoreDemo(companyId: number, storeId: number, isDemo: boolean) {
    if (togglingStoreIds.has(storeId)) return;
    setTogglingStoreIds((prev) => new Set(prev).add(storeId));
    try {
      await superAdminApi.toggleStoreDemo(storeId, isDemo);
      setStoresByCompany((prev) => ({
        ...prev,
        [companyId]: (prev[companyId] ?? []).map((s) => (s.id === storeId ? { ...s, isDemo } : s)),
      }));
    } catch {
      // handled by global toast
    } finally {
      setTogglingStoreIds((prev) => {
        const next = new Set(prev);
        next.delete(storeId);
        return next;
      });
    }
  }

  async function addStore(companyId: number) {
    if (!newStoreName.trim()) return;
    setSavingStore(true);
    try {
      const store = await superAdminApi.createStore(companyId, newStoreName.trim());
      setStoresByCompany((prev) => ({
        ...prev,
        [companyId]: [...(prev[companyId] ?? []), store],
      }));
      setCompanies((prev) =>
        prev.map((c) => (c.id === companyId ? { ...c, storeCount: c.storeCount + 1 } : c)),
      );
      setNewStoreName('');
      setAddingStoreForCompanyId(null);
    } catch {
      // handled by global toast
    } finally {
      setSavingStore(false);
    }
  }

  async function deleteStore(companyId: number, storeId: number) {
    if (!confirm('Delete this store?')) return;
    if (deletingStoreIds.has(storeId)) return;
    setDeletingStoreIds((prev) => new Set(prev).add(storeId));
    try {
      await superAdminApi.deleteStore(storeId);
      setStoresByCompany((prev) => ({
        ...prev,
        [companyId]: (prev[companyId] ?? []).filter((s) => s.id !== storeId),
      }));
      setCompanies((prev) =>
        prev.map((c) => (c.id === companyId ? { ...c, storeCount: c.storeCount - 1 } : c)),
      );
    } catch {
      // handled by global toast
    } finally {
      setDeletingStoreIds((prev) => {
        const next = new Set(prev);
        next.delete(storeId);
        return next;
      });
    }
  }

  async function deleteCompany(id: number, name: string) {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      await superAdminApi.deleteCompany(id);
      setCompanies((prev) => prev.filter((c) => c.id !== id));
    } catch {
      // handled by global toast
    }
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="flex items-center gap-3 mb-8">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">SUPER ADMIN</h1>
            <p className="text-red-300 text-xs">Company Management</p>
          </div>
        </header>

        {/* Provision New Company */}
        <div className="glass-card rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Provision New Company</h2>
            <button
              onClick={() => {
                setShowProvision(!showProvision);
                setProvisionResult(null);
              }}
              className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20 text-sm"
            >
              {showProvision ? 'Cancel' : '+ New'}
            </button>
          </div>

          {provisionResult && (
            <div
              className="mb-4 p-4 rounded-lg space-y-1"
              style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}
            >
              <p className="text-green-400 text-sm font-medium">
                ✓ {provisionResult.companyName} provisioned successfully
              </p>
              <p className="text-gray-400 text-xs">
                {provisionResult.userEmail} can now sign in via email OTP at the app URL.
              </p>
            </div>
          )}

          {showProvision && (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Company name"
                value={provisionForm.companyName}
                onChange={(e) => setProvisionForm((f) => ({ ...f, companyName: e.target.value }))}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              />
              <input
                type="email"
                placeholder="Admin email address"
                value={provisionForm.userEmail}
                onChange={(e) => setProvisionForm((f) => ({ ...f, userEmail: e.target.value }))}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              />
              <input
                type="text"
                placeholder="SOUNDRAW API token"
                value={provisionForm.soundrawToken}
                onChange={(e) => setProvisionForm((f) => ({ ...f, soundrawToken: e.target.value }))}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              />
              <input
                type="text"
                placeholder="Token description (optional, e.g. acme-corp-prod)"
                value={provisionForm.tokenDescription}
                onChange={(e) =>
                  setProvisionForm((f) => ({ ...f, tokenDescription: e.target.value }))
                }
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              />
              <button
                onClick={() => void provision()}
                disabled={
                  provisioning ||
                  !provisionForm.companyName ||
                  !provisionForm.userEmail ||
                  !provisionForm.soundrawToken
                }
                className="w-full py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}
              >
                {provisioning ? 'Provisioning...' : 'Provision Company'}
              </button>
            </div>
          )}
        </div>

        {/* Companies */}
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Companies ({companies.length})</h2>
            <button
              onClick={() => void refresh()}
              className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20 text-sm"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div
                className="w-8 h-8 border-4 rounded-full animate-spin"
                style={{ borderColor: 'rgba(220,38,38,0.3)', borderTopColor: '#dc2626' }}
              />
            </div>
          ) : companies.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No companies registered</p>
          ) : (
            <div className="space-y-4">
              {companies.map((company) => (
                <div key={company.id} className="bg-white/5 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-white">{company.name}</h3>
                      <p className="text-gray-400 text-sm">{company.clerkOrgId}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        Stores: {company.storeCount} · Created:{' '}
                        {new Date(company.createdAt * 1000).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => void deleteCompany(company.id, company.name)}
                      className="px-4 py-2 rounded-lg text-white text-sm"
                      style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}
                    >
                      Delete
                    </button>
                  </div>

                  {/* SOUNDRAW Token */}
                  {editingTokenId === company.id ? (
                    <div className="space-y-2 pt-2 border-t border-white/10">
                      <input
                        type="text"
                        value={tokenDescInput}
                        onChange={(e) => setTokenDescInput(e.target.value)}
                        placeholder="Description (e.g. Company A token)"
                        className="w-full bg-white/5 border border-white/20 rounded px-3 py-1.5 text-white text-sm focus:outline-none"
                      />
                      <input
                        type="password"
                        value={tokenInput}
                        onChange={(e) => setTokenInput(e.target.value)}
                        placeholder="SOUNDRAW API token (leave blank to keep existing)"
                        className="w-full bg-white/5 border border-white/20 rounded px-3 py-1.5 text-white text-sm focus:outline-none"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => void saveToken(company.id)}
                          disabled={tokenSaving}
                          className="px-3 py-1.5 rounded text-white text-sm bg-green-700 hover:bg-green-600 disabled:opacity-50"
                        >
                          {tokenSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingTokenId(null)}
                          className="px-3 py-1.5 rounded text-white text-sm bg-white/10 hover:bg-white/20"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between pt-2 border-t border-white/10">
                      <div className="text-xs">
                        {company.hasToken ? (
                          <span className="text-green-400">
                            ✓ Token set
                            {company.tokenDescription ? `: ${company.tokenDescription}` : ''}
                          </span>
                        ) : (
                          <span className="text-red-400">
                            ⚠ No token set — generation will fail
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => startEditToken(company)}
                        className="px-3 py-1 rounded text-white text-xs bg-white/10 hover:bg-white/20"
                      >
                        {company.hasToken ? 'Edit Token' : 'Set Token'}
                      </button>
                    </div>
                  )}

                  {/* Stores / demo mode */}
                  <div className="pt-2 border-t border-white/10">
                    <button
                      onClick={() => void toggleStores(company.id)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
                    >
                      <svg
                        className={`w-3 h-3 transition-transform ${expandedStoresId === company.id ? 'rotate-90' : ''}`}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      Stores ({company.storeCount})
                    </button>

                    {expandedStoresId === company.id && (
                      <div className="mt-2 space-y-1.5">
                        {loadingStoresId === company.id ? (
                          <div className="pl-4">
                            <div
                              className="w-4 h-4 border-2 rounded-full animate-spin"
                              style={{
                                borderColor: 'rgba(142,152,223,0.3)',
                                borderTopColor: '#8E98DF',
                              }}
                            />
                          </div>
                        ) : (
                          <>
                            {(storesByCompany[company.id] ?? []).length === 0 ? (
                              <p className="text-xs text-gray-500 pl-4">No stores</p>
                            ) : (
                              (storesByCompany[company.id] ?? []).map((store) => (
                                <div key={store.id} className="pl-4 pr-1 space-y-1.5">
                                  {/* Name row */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-white">{store.name}</span>
                                      {store.isDemo && (
                                        <span
                                          className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                                          style={{
                                            background: 'rgba(255,251,147,0.15)',
                                            color: '#FFFB93',
                                            border: '1px solid rgba(255,251,147,0.3)',
                                          }}
                                        >
                                          DEMO
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-400">Demo Mode</span>
                                      <button
                                        onClick={() =>
                                          void toggleStoreDemo(company.id, store.id, !store.isDemo)
                                        }
                                        disabled={togglingStoreIds.has(store.id)}
                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${store.isDemo ? 'bg-[#FFFB93]' : 'bg-white/20'}`}
                                        role="switch"
                                        aria-checked={store.isDemo}
                                      >
                                        <span
                                          className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${store.isDemo ? 'translate-x-5' : 'translate-x-1'}`}
                                        />
                                      </button>
                                      <button
                                        onClick={() => void deleteStore(company.id, store.id)}
                                        disabled={deletingStoreIds.has(store.id)}
                                        className="text-red-400 hover:text-red-300 disabled:opacity-50 p-0.5"
                                        title="Delete store"
                                      >
                                        <svg
                                          className="w-3.5 h-3.5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                  {/* Created date */}
                                  <p className="text-xs text-gray-500 pl-0.5">
                                    Created: {new Date(store.createdAt * 1000).toLocaleDateString()}
                                  </p>
                                  {/* URL row */}
                                  <div className="flex items-center gap-1.5 bg-black/20 rounded px-2 py-1">
                                    <code
                                      className="flex-1 text-xs truncate"
                                      style={{ color: '#29ABE2' }}
                                    >
                                      {storeUrl(store.accessToken)}
                                    </code>
                                    <button
                                      onClick={() => copyStoreUrl(store.id, store.accessToken)}
                                      className="shrink-0 text-gray-400 hover:text-white"
                                      title="Copy URL"
                                    >
                                      {copiedStoreId === store.id ? (
                                        <svg
                                          className="w-3.5 h-3.5"
                                          style={{ color: '#FFFB93' }}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      ) : (
                                        <svg
                                          className="w-3.5 h-3.5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                          />
                                        </svg>
                                      )}
                                    </button>
                                    <a
                                      href={storeUrl(store.accessToken)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="shrink-0 text-gray-400 hover:text-white"
                                      title="Open"
                                    >
                                      <svg
                                        className="w-3.5 h-3.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    </a>
                                  </div>
                                </div>
                              ))
                            )}

                            {/* Add store */}
                            {addingStoreForCompanyId === company.id ? (
                              <div className="flex items-center gap-2 pl-4 pr-1 pt-1">
                                <input
                                  type="text"
                                  value={newStoreName}
                                  onChange={(e) => setNewStoreName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !savingStore)
                                      void addStore(company.id);
                                    if (e.key === 'Escape') {
                                      setAddingStoreForCompanyId(null);
                                      setNewStoreName('');
                                    }
                                  }}
                                  placeholder="Store name"
                                  className="flex-1 bg-white/5 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none"
                                  autoFocus
                                />
                                <button
                                  onClick={() => void addStore(company.id)}
                                  disabled={savingStore || !newStoreName.trim()}
                                  className="px-2 py-1 rounded text-white text-xs bg-green-700 hover:bg-green-600 disabled:opacity-50"
                                >
                                  {savingStore ? '...' : 'Add'}
                                </button>
                                <button
                                  onClick={() => {
                                    setAddingStoreForCompanyId(null);
                                    setNewStoreName('');
                                  }}
                                  className="px-2 py-1 rounded text-white text-xs bg-white/10 hover:bg-white/20"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setAddingStoreForCompanyId(company.id);
                                  setNewStoreName('');
                                }}
                                className="flex items-center gap-1 text-xs text-gray-500 hover:text-white pl-4 transition-colors"
                              >
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                  />
                                </svg>
                                Add Store
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Warning */}
        <div
          className="mt-6 p-4 rounded-lg"
          style={{ background: 'rgba(153,27,27,0.3)', border: '1px solid rgba(220,38,38,0.3)' }}
        >
          <p className="text-red-300 text-sm">
            ⚠️ Deleting a company will permanently remove all associated stores and data. This
            action cannot be undone.
          </p>
        </div>
      </div>
    </div>
  );
}
